import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimeTracker = ({ requestId }) => {
  const [expanded, setExpanded] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    let interval;
    if (isTracking && startTime) {
      interval = setInterval(() => {
        const now = new Date()?.getTime();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTracking, startTime]);

  const handleStartTracking = () => {
    setIsTracking(true);
    setStartTime(new Date()?.getTime());
    setElapsedTime(0);
  };

  const handleStopTracking = () => {
    if (startTime) {
      const session = {
        id: Date.now(),
        startTime: new Date(startTime),
        endTime: new Date(),
        duration: elapsedTime
      };
      setSessions(prev => [session, ...prev]);
    }
    setIsTracking(false);
    setStartTime(null);
    setElapsedTime(0);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getTotalTime = () => {
    const sessionTotal = sessions?.reduce((sum, session) => sum + session?.duration, 0);
    return sessionTotal + (isTracking ? elapsedTime : 0);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={20} color="var(--color-primary)" strokeWidth={2} />
          <h2 className="text-lg font-semibold text-foreground">Time Tracker</h2>
          <span className="text-sm text-muted-foreground">
            Total: {formatDuration(getTotalTime())}
          </span>
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={20} strokeWidth={2} />
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Current Timer */}
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="text-4xl font-bold text-foreground mb-4">
              {formatDuration(elapsedTime)}
            </div>
            {isTracking ? (
              <Button
                variant="destructive"
                size="lg"
                iconName="Square"
                iconPosition="left"
                onClick={handleStopTracking}
                fullWidth
              >
                Stop Timer
              </Button>
            ) : (
              <Button
                variant="default"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={handleStartTracking}
                fullWidth
              >
                Start Timer
              </Button>
            )}
          </div>

          {/* Sessions List */}
          {sessions?.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Work Sessions</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sessions?.map(session => (
                  <div key={session?.id} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formatDuration(session?.duration)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTime(session?.startTime)} - {formatTime(session?.endTime)}
                      </p>
                    </div>
                    <Icon name="Clock" size={16} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Total Work Time</span>
              <span className="text-lg font-bold text-primary">{formatDuration(getTotalTime())}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTracker;